import React, { Fragment, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../../../components/Header';
import Load from '../../../components/Load';
import { useProfile } from '../../../context/ProfileContext';
import { useLigaPermissions } from '../../../hooks/useLigaPermissions';
import { useFinanceiroLiga } from '../../../hooks/useFinanceiroLiga';
import ResumoLigaTab from './ResumoLigaTab';
import RecebimentosLigaTab from './RecebimentosLigaTab';
import APagarInPlayTab from './APagarInPlayTab';
import ReembolsosLigaTab from './ReembolsosLigaTab';

// Page de orquestração do hub financeiro da liga — molde exato de
// pages/atleta/financeiro/FinanceiroHub.tsx (?tab= + replace: true), com o GATE de
// LigaPaymentsDashboard.tsx:113-136 preservado byte-a-byte (mesma ordem, mesma mensagem de
// negação — AC-A6). Chama useFinanceiroLiga(ligaId) uma única vez e repassa tudo por props para
// a aba ativa; nenhuma aba busca resumo/serie/quebra por conta própria — Recebimentos,
// "A pagar à InPlay" e Reembolsos têm, cada uma, sua própria consulta paginada/filtrada, fora do
// trio resumo/serie/quebra. A aba "Saldo e saques" (esqueleto da F-8) É DELIBERADAMENTE ausente
// de `TABS` — ver pages/liga/financeiro/SaldoSaquesTab.tsx (AC-A4).

type TabId = 'resumo' | 'recebimentos' | 'a-pagar' | 'reembolsos';

const TABS: { id: TabId; label: string }[] = [
  { id: 'resumo', label: 'Resumo' },
  { id: 'recebimentos', label: 'Recebimentos' },
  { id: 'a-pagar', label: 'A pagar à InPlay' },
  { id: 'reembolsos', label: 'Reembolsos' },
];

// AC-A2 (PRD AC-14) — chave global (não escopada por liga, de propósito: a nota comunica uma
// mudança sistêmica de cálculo — agregação no servidor —, não algo específico de uma liga em
// particular). Contrato fixado no "## Notes" do plano da fase 6, consumido literalmente pela
// suíte de teste (FinanceiroLigaHub.divergencia.test.tsx).
const NOTA_DIVERGENCIA_KEY = 'financeiro-liga-nota-divergencia-dispensada';

const FinanceiroLigaHub: React.FC = () => {
  const { currentProfile, isLoading: profileLoading } = useProfile();
  const ligaId = currentProfile?.tipo === 'LIGA' ? currentProfile.referenciaId : null;

  const { canManagePayments, isLoading: permissionsLoading } = useLigaPermissions(ligaId);

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  // ?tab= ausente ou desconhecido cai em Resumo, sem quebrar (AC-A7).
  const activeTab: TabId = TABS.some((tab) => tab.id === tabParam) ? (tabParam as TabId) : 'resumo';
  const irParaAba = (id: TabId) => setSearchParams({ tab: id }, { replace: true });

  const { periodo, resumo, serie, quebra, loading, error, resumoIndisponivel, refetch } =
    useFinanceiroLiga(ligaId ?? '');

  // Lida uma única vez no mount (não `useEffect`) para não haver flash do banner antes de
  // esconder — "primeira carga", não "toda carga" (AC-A2).
  const [notaDivergenciaVisivel, setNotaDivergenciaVisivel] = useState(
    () => localStorage.getItem(NOTA_DIVERGENCIA_KEY) !== 'true',
  );

  const dispensarNotaDivergencia = () => {
    localStorage.setItem(NOTA_DIVERGENCIA_KEY, 'true');
    setNotaDivergenciaVisivel(false);
  };

  if (profileLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left" style={{ maxWidth: '1200px' }}>
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!currentProfile || currentProfile.tipo !== 'LIGA') {
    return null;
  }

  if (!canManagePayments && !permissionsLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left" style={{ maxWidth: '1200px' }}>
              <div className="row feed-body">
                <div className="col-xl-12">
                  <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3 mt-3">
                    <div className="card-body p-4">
                      <div className="alert alert-danger" role="alert">
                        Acesso não autorizado. Apenas administradores e financeiros podem acessar esta página.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (permissionsLoading) {
    return (
      <>
        <Header />
        <div className="main-content right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left" style={{ maxWidth: '1200px' }}>
              <div className="row feed-body">
                <div className="col-xl-12">
                  <Load />
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <Fragment>
      <div className="main-wrapper">
        <Header />

        <div className="main-content theme-dark-bg right-chat-active">
          <div className="middle-sidebar-bottom">
            <div className="middle-sidebar-left">
              <div className="middle-wrap">
                <div className="mb-3">
                  <h3 className="fw-700 mb-0">Financeiro da liga</h3>
                </div>

                {notaDivergenciaVisivel && (
                  // `role="status"`, e NÃO `role="alert"` como a Task 6 do plano da fase 6 dizia
                  // literalmente (espelhando pages/atleta/Payments.tsx:117). Desvio deliberado, por
                  // dois motivos: (1) `role="alert"` já é reservado NESTE arquivo para a mensagem de
                  // acesso negado (linha 97), e o teste da fase 3 trava esse escopo —
                  // FinanceiroLigaHub.test.tsx asserta `queryByRole('alert')` nulo no caminho feliz,
                  // que é exatamente onde este banner monta; usar `alert` aqui quebraria um teste
                  // pré-existente. (2) ARIA: `alert` é assertivo, para o que interrompe o usuário;
                  // um aviso informativo e dispensável é `status` (live region polida). Nem a PRD
                  // AC-14 nem o plano exigem role ARIA algum. Revisado e aceito em code review
                  // (R-SEM, achado de baixa severidade, não-bloqueante).
                  <div
                    className="alert alert-info d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3"
                    role="status"
                  >
                    <span>
                      Os totais agora são agregados no servidor e podem divergir do número
                      anterior, que somava apenas a página carregada.
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={dispensarNotaDivergencia}
                    >
                      Dispensar
                    </button>
                  </div>
                )}

                <ul className="nav nav-tabs mb-3" role="tablist">
                  {TABS.map((tab) => (
                    <li className="nav-item" key={tab.id} role="presentation">
                      <button
                        type="button"
                        className={`nav-link${activeTab === tab.id ? ' active' : ''}`}
                        onClick={() => irParaAba(tab.id)}
                        role="tab"
                        aria-selected={activeTab === tab.id}
                      >
                        {tab.label}
                      </button>
                    </li>
                  ))}
                </ul>

                {activeTab === 'resumo' && (
                  <ResumoLigaTab
                    resumo={resumo}
                    serie={serie}
                    quebra={quebra}
                    loading={loading}
                    error={error}
                    resumoIndisponivel={resumoIndisponivel}
                    onRetry={refetch}
                    periodo={periodo}
                  />
                )}
                {activeTab === 'recebimentos' && ligaId && (
                  <RecebimentosLigaTab ligaId={ligaId} quebra={quebra} />
                )}
                {activeTab === 'a-pagar' && ligaId && <APagarInPlayTab ligaId={ligaId} />}
                {activeTab === 'reembolsos' && ligaId && <ReembolsosLigaTab ligaId={ligaId} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default FinanceiroLigaHub;
